<template lang="pug">
  dv-maska-field(
    v-model="inputValue"
    v-bind="extraAttrs"
    v-on="$listeners"
  )
    template(
      v-for="(_, name) in $scopedSlots"
      v-slot:[name]="data"
    )
      slot(:name="name" v-bind="data")
    template(
      v-for="(_, name) in $slots"
      :slot="name"
    )
      slot(:name="name")

    template(v-slot:append)
      slot(
        name="append"
        :is-min="isMin"
        :is-max="isMax"
      )

    template(v-slot:prepend-inner)
      slot(
        name="prepend-inner"
        :is-min="isMin"
        :is-max="isMax"
      )
</template>

<script>
import DvMaskaField from './DvMaskaField.vue';
import isNil from 'lodash/isNil';

export default {
  components: {
    DvMaskaField,
  },
  props: {
    min: {
      type: Number,
      default: undefined,
    },
    max: {
      type: Number,
      default: undefined,
    },
    defaultToMin: {
      type: Boolean,
      default: false,
    },
    float: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
      default: undefined,
    },
  },
  data() {
    return {
      inputValue: undefined,
    };
  },
  watch: {
    value: {
      handler(val) {
        this.setValue(val);
      },
      immediate: true,
    },
    inputValue: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.$emit('input', newValue);
        }
      },
      immediate: true,
    },
    defaultToMin: {
      handler(val) {
        if (val) this.inputValue = this.min;
      },
      immediate: true,
    },
  },
  computed: {
    minValue() {
      const { min } = this;
      return isNil(min) ? Number.MIN_SAFE_INTEGER : min;
    },
    maxValue() {
      const { max } = this;
      return isNil(max) ? Number.MAX_SAFE_INTEGER : max;
    },
    isMin() {
      return this.inputValue === this.minValue;
    },
    isMax() {
      return this.inputValue === this.maxValue;
    },
    maskaOptions() {
      const { float } = this;
      return {
        mask: [
          'N',
          ...float ? ['?##'] : [],
        ].join(''),
        tokens: {
          N: {
            pattern: /[-0-9]/,
            multiple: true,
          },
          '?': {
            pattern: /[.,]/,
          },
          '#': {
            pattern: /[0-9]/,
          },
        },
        preProcess: (v) => v.replace(/(?!^)-/g, '').replace(',', '.'),
        postProcess: (v) => {
          const { minValue, maxValue } = this;

          if (isNil(v) || (typeof v === 'string' && !v)) return v;

          if (v < minValue) return minValue.toString();
          if (v > maxValue) return maxValue.toString();

          return v;
        },
        serializer: (v) => {
          const outputValue = Number(v);

          if (
            isNil(v)
            || Number.isNaN(outputValue)
            || (typeof v === 'string' && !v)
          ) return this.defaultToMin ? this.minValue : undefined;

          return outputValue;
        },
      };
    },
    extraAttrs() {
      const { $attrs, maskaOptions } = this;

      return {
        ...$attrs,
        maskaOptions,
      };
    },
  },
  methods: {
    setValue(val) {
      this.inputValue = this.getValue(val);
    },
    getValue(val) {
      const { minValue, maxValue } = this;

      if (val < minValue) {
        return minValue;
      }

      if (val > maxValue) {
        return maxValue;
      }

      return val;
    },
  },
};
</script>
